import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API } from "../App";
import { ClickableUser } from "../contexts/UserSheetContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { toast } from "sonner";
import {
  TrendingUp,
  Save,
  Loader2,
  Users,
  Euro,
  Percent,
  ArrowUpRight,
  Zap,
  BarChart3,
  RefreshCw,
} from "lucide-react";
import {
  ResponsiveContainer,
  ComposedChart,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from "recharts";

// ── Helpers ────────────────────────────────────────────────────────────────────

const getInitials = (name) =>
  (name || "?").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();

const fmtEuro = (val) => {
  if (val == null || val === 0 || isNaN(val)) return "—";
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(val);
};

const fmtPct = (val) => {
  if (val == null || isNaN(val)) return "—";
  return `${val > 0 ? "+" : ""}${val.toFixed(2)} %`;
};

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = [CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1];

// ── Custom Tooltip ─────────────────────────────────────────────────────────────

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const actuel = payload.find((p) => p.dataKey === "Actuel")?.value || 0;
  const nouveau = payload.find((p) => p.dataKey === "Nouveau")?.value || 0;
  const diff = nouveau - actuel;
  const pct = actuel > 0 ? ((diff / actuel) * 100).toFixed(2) : 0;
  return (
    <div className="bg-white dark:bg-[#112240] border border-gray-200 dark:border-[#1A3050] rounded-lg p-3 shadow-lg text-xs space-y-1 min-w-[160px]">
      <p className="font-semibold text-[#111827] dark:text-[#D6E4F5]">
        {payload[0]?.payload?.fullName}
      </p>
      <p className="text-[#9CA3AF]">Actuel : {fmtEuro(actuel)}</p>
      <p className="text-[#1A4D2E] dark:text-[#4CAF70] font-medium">
        Nouveau : {fmtEuro(nouveau)}
      </p>
      {diff !== 0 && (
        <p className={diff > 0 ? "text-emerald-600 dark:text-emerald-400 font-medium" : "text-red-500 font-medium"}>
          {diff > 0 ? "+" : ""}{fmtEuro(diff)} ({diff > 0 ? "+" : ""}{pct}%)
        </p>
      )}
    </div>
  );
};

// ── Page ───────────────────────────────────────────────────────────────────────

// Palette de couleurs pour les lignes individuelles
const LINE_COLORS = [
  "#1A4D2E", "#3B82F6", "#E07A5F", "#8B5CF6", "#F59E0B",
  "#06B6D4", "#10B981", "#EF4444", "#EC4899", "#6366F1",
  "#84CC16", "#F97316", "#14B8A6", "#A855F7", "#0EA5E9",
];

const NAOPage = () => {
  const [collaborateurs, setCollaborateurs] = useState([]);
  const [history, setHistory] = useState({ by_year: [], by_employee: [] });
  const [histLoading, setHistLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [year, setYear] = useState(CURRENT_YEAR);
  const [globalPct, setGlobalPct] = useState("");
  const [deptFilter, setDeptFilter] = useState("all");

  const fetchCollaborateurs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/nao/collaborateurs`);
      const data = response.data.map((c) => ({
        ...c,
        new_gross_salary: c.current_gross != null ? String(Math.round(c.current_gross)) : "",
        new_pct: "0",
        new_bonus: "",
        new_salary_type: c.salary_type || "mensuel",
        new_note: "",
      }));
      setCollaborateurs(data);
    } catch {
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/nao/history`);
      setHistory(res.data);
    } catch {
      // silencieux si pas de données
    } finally {
      setHistLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCollaborateurs();
    fetchHistory();
  }, [fetchCollaborateurs, fetchHistory]);

  // Champ générique (prime, note, type)
  const update = (userId, field, value) =>
    setCollaborateurs((prev) =>
      prev.map((c) => (c.user_id === userId ? { ...c, [field]: value } : c))
    );

  // Modification du nouveau brut → recalcule le %
  const updateGross = (userId, value) =>
    setCollaborateurs((prev) =>
      prev.map((c) => {
        if (c.user_id !== userId) return c;
        const gross = parseFloat(value) || 0;
        const pct =
          c.current_gross > 0
            ? ((gross - c.current_gross) / c.current_gross) * 100
            : 0;
        return {
          ...c,
          new_gross_salary: value,
          new_pct: isNaN(pct) ? "0" : String(parseFloat(pct.toFixed(2))),
        };
      })
    );

  // Modification du % → recalcule le nouveau brut
  const updatePct = (userId, value) =>
    setCollaborateurs((prev) =>
      prev.map((c) => {
        if (c.user_id !== userId) return c;
        const pct = parseFloat(value);
        const gross =
          c.current_gross != null && !isNaN(pct)
            ? String(Math.round(c.current_gross * (1 + pct / 100)))
            : c.new_gross_salary;
        return { ...c, new_pct: value, new_gross_salary: gross };
      })
    );

  // Apply a global % to all collaborateurs
  const applyGlobalIncrease = () => {
    const pct = parseFloat(globalPct);
    if (isNaN(pct) || pct <= 0) {
      toast.warning("Saisissez un pourcentage valide (> 0)");
      return;
    }
    setCollaborateurs((prev) =>
      prev.map((c) => ({
        ...c,
        new_gross_salary: c.current_gross
          ? String(Math.round(c.current_gross * (1 + pct / 100)))
          : c.new_gross_salary,
        new_pct: c.current_gross ? String(pct) : c.new_pct,
      }))
    );
    toast.success(`+${pct}% appliqué à tous les collaborateurs`);
  };

  const resetAll = () => {
    setCollaborateurs((prev) =>
      prev.map((c) => ({
        ...c,
        new_gross_salary: c.current_gross != null ? String(Math.round(c.current_gross)) : "",
        new_pct: "0",
        new_bonus: "",
        new_note: "",
      }))
    );
    setGlobalPct("");
    toast.info("Réinitialisé");
  };

  const handleSave = async () => {
    const entries = collaborateurs.map((c) => ({
      user_id: c.user_id,
      gross_salary: parseFloat(c.new_gross_salary) || 0,
      bonus: c.new_bonus ? parseFloat(c.new_bonus) : null,
      salary_type: c.new_salary_type,
      note: c.new_note || "",
    }));
    setSaving(true);
    try {
      await axios.post(`${API}/nao/apply`, { year, entries });
      toast.success(`NAO ${year} appliquée avec succès`);
      fetchCollaborateurs();
      fetchHistory();
    } catch {
      toast.error("Erreur lors de l'application des salaires");
    } finally {
      setSaving(false);
    }
  };

  // ── KPIs ─────────────────────────────────────────────────────────────────────

  const totalCurrentMass = collaborateurs.reduce((s, c) => s + (c.current_gross || 0), 0);
  const totalNewMass = collaborateurs.reduce(
    (s, c) => s + (parseFloat(c.new_gross_salary) || 0),
    0
  );
  const totalBonus = collaborateurs.reduce(
    (s, c) => s + (parseFloat(c.new_bonus) || 0),
    0
  );
  const masseDiff = totalNewMass - totalCurrentMass;
  const globalIncreasePct =
    totalCurrentMass > 0 ? (masseDiff / totalCurrentMass) * 100 : 0;

  // ── Chart data ────────────────────────────────────────────────────────────────

  const chartData = collaborateurs
    .filter((c) => c.current_gross || parseFloat(c.new_gross_salary))
    .map((c) => {
      // Nom complet tronqué à 20 car. pour l'axe Y horizontal
      const label =
        c.full_name.length > 20 ? c.full_name.slice(0, 18) + "…" : c.full_name;
      return {
        name: label,
        fullName: c.full_name,
        Actuel: c.current_gross || 0,
        Nouveau: parseFloat(c.new_gross_salary) || 0,
      };
    });

  const globalPctFloat = parseFloat(globalPct);
  const projectedMass =
    !isNaN(globalPctFloat) && globalPctFloat > 0
      ? Math.round(totalCurrentMass * (1 + globalPctFloat / 100))
      : null;

  // ── Historique — données pivotées pour le graphique individuel ────────────────

  const departments = useMemo(
    () => [...new Set((history.by_employee || []).map((e) => e.department).filter(Boolean))].sort(),
    [history.by_employee]
  );

  const filteredEmployees = useMemo(
    () =>
      deptFilter === "all"
        ? history.by_employee
        : history.by_employee.filter((e) => e.department === deptFilter),
    [history.by_employee, deptFilter]
  );

  // Pivot: [{ year, u_1: 3000, u_2: 2500 }, ...]
  const pivotData = useMemo(() => {
    if (!history.by_employee.length) return [];
    const yearsSet = new Set();
    history.by_employee.forEach((emp) => (emp.history || []).forEach((y) => yearsSet.add(y.year)));
    return [...yearsSet].sort().map((yr) => {
      const entry = { year: yr };
      history.by_employee.forEach((emp) => {
        const found = (emp.history || []).find((y) => y.year === yr);
        if (found) entry[`u_${emp.user_id}`] = found.gross_salary;
      });
      return entry;
    });
  }, [history.by_employee]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#1A4D2E]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2 dark:text-[#D6E4F5]">
            <TrendingUp className="h-7 w-7 text-[#1A4D2E] dark:text-[#4CAF70]" />
            NAO — Négociation Annuelle Obligatoire
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Suivi des rémunérations et négociation des augmentations annuelles
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2">
            <Label className="text-sm whitespace-nowrap dark:text-[#D6E4F5]">Année</Label>
            <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
              <SelectTrigger className="w-28 dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {YEAR_OPTIONS.map((y) => (
                  <SelectItem key={y} value={y.toString()}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="outline"
            onClick={resetAll}
            className="gap-2 dark:border-[#1A3050] dark:text-[#D6E4F5] dark:hover:bg-[#112240]"
          >
            <RefreshCw className="h-4 w-4" />
            <span className="hidden sm:inline">Réinitialiser</span>
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#1A4D2E] hover:bg-[#143D24] text-white gap-2"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Appliquer la NAO
          </Button>
        </div>
      </div>

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Card className="dark:bg-[#0B1628] dark:border-[#1A3050]">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Euro className="w-4 h-4 text-[#9CA3AF] shrink-0" />
              <span className="text-xs text-[#9CA3AF] dark:text-[#7EA8CE] leading-tight">Masse actuelle</span>
            </div>
            <p className="text-lg sm:text-xl font-bold dark:text-[#D6E4F5] tabular-nums">
              {fmtEuro(totalCurrentMass)}
            </p>
            <p className="text-xs text-[#9CA3AF] mt-1">{collaborateurs.length} collaborateur(s)</p>
          </CardContent>
        </Card>

        <Card className="dark:bg-[#0B1628] dark:border-[#1A3050]">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-4 h-4 text-[#1A4D2E] dark:text-[#4CAF70] shrink-0" />
              <span className="text-xs text-[#9CA3AF] dark:text-[#7EA8CE] leading-tight">Masse après NAO</span>
            </div>
            <p className="text-lg sm:text-xl font-bold dark:text-[#D6E4F5] tabular-nums">
              {fmtEuro(totalNewMass)}
            </p>
            <p
              className={`text-xs mt-1 tabular-nums ${
                masseDiff > 0
                  ? "text-emerald-600 dark:text-emerald-400"
                  : "text-[#9CA3AF]"
              }`}
            >
              {masseDiff !== 0 ? `${masseDiff > 0 ? "+" : ""}${fmtEuro(masseDiff)}` : "Aucun changement"}
            </p>
          </CardContent>
        </Card>

        <Card
          className={`dark:border-[#1A3050] ${
            globalIncreasePct > 0
              ? "bg-emerald-50 dark:bg-emerald-900/10 border-emerald-200 dark:border-emerald-800/30"
              : "dark:bg-[#0B1628]"
          }`}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Percent className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
              <span className="text-xs text-[#9CA3AF] dark:text-[#7EA8CE] leading-tight">Augmentation globale</span>
            </div>
            <p
              className={`text-lg sm:text-xl font-bold tabular-nums ${
                globalIncreasePct > 0
                  ? "text-emerald-700 dark:text-emerald-400"
                  : "dark:text-[#D6E4F5]"
              }`}
            >
              {fmtPct(globalIncreasePct)}
            </p>
            <p className="text-xs text-[#9CA3AF] mt-1">Sur la masse brute totale</p>
          </CardContent>
        </Card>

        <Card className="dark:bg-[#0B1628] dark:border-[#1A3050]">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <ArrowUpRight className="w-4 h-4 text-amber-500 shrink-0" />
              <span className="text-xs text-[#9CA3AF] dark:text-[#7EA8CE] leading-tight">Primes NAO {year}</span>
            </div>
            <p className="text-lg sm:text-xl font-bold dark:text-[#D6E4F5] tabular-nums">
              {totalBonus > 0 ? fmtEuro(totalBonus) : "—"}
            </p>
            <p className="text-xs text-[#9CA3AF] mt-1">Budget primes total</p>
          </CardContent>
        </Card>
      </div>

      {/* ── Outil global + Graphique ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Outil augmentation globale */}
        <Card className="dark:bg-[#0B1628] dark:border-[#1A3050]">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2 dark:text-[#D6E4F5]">
              <Zap className="w-4 h-4 text-amber-500" />
              Augmentation globale
            </CardTitle>
            <CardDescription className="text-xs">
              Appliquer un pourcentage d'augmentation identique à tous les collaborateurs, puis ajuster individuellement.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  type="number"
                  placeholder="Ex : 3.5"
                  value={globalPct}
                  onChange={(e) => setGlobalPct(e.target.value)}
                  className="pr-8 dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]"
                  min="0"
                  max="100"
                  step="0.1"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] text-sm font-medium">
                  %
                </span>
              </div>
              <Button
                onClick={applyGlobalIncrease}
                disabled={!globalPct}
                className="bg-[#1A4D2E] hover:bg-[#143D24] text-white shrink-0"
              >
                Répartir
              </Button>
            </div>

            {projectedMass && totalCurrentMass > 0 && (
              <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/10 p-3 text-xs space-y-1.5 border border-emerald-200 dark:border-emerald-800/30">
                <p className="font-semibold text-emerald-700 dark:text-emerald-400">
                  Simulation +{globalPctFloat}%
                </p>
                <div className="flex justify-between text-[#4B5563] dark:text-[#7EA8CE]">
                  <span>Masse actuelle</span>
                  <span className="tabular-nums">{fmtEuro(totalCurrentMass)}</span>
                </div>
                <div className="flex justify-between text-[#4B5563] dark:text-[#7EA8CE]">
                  <span>Masse projetée</span>
                  <span className="tabular-nums">{fmtEuro(projectedMass)}</span>
                </div>
                <div className="flex justify-between font-semibold text-emerald-700 dark:text-emerald-400 border-t border-emerald-200 dark:border-emerald-800/30 pt-1.5 mt-1">
                  <span>Coût supplémentaire / mois</span>
                  <span className="tabular-nums">+{fmtEuro(projectedMass - totalCurrentMass)}</span>
                </div>
                <div className="flex justify-between font-semibold text-emerald-700 dark:text-emerald-400">
                  <span>Coût annuel estimé</span>
                  <span className="tabular-nums">+{fmtEuro((projectedMass - totalCurrentMass) * 12)}</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Graphique */}
        <Card className="lg:col-span-2 dark:bg-[#0B1628] dark:border-[#1A3050]">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2 dark:text-[#D6E4F5]">
              <BarChart3 className="w-4 h-4 text-[#1A4D2E] dark:text-[#4CAF70]" />
              Salaires bruts : Actuel vs Nouveau
            </CardTitle>
            <CardDescription className="text-xs">
              Comparaison par collaborateur. Survolez pour les détails.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            {chartData.length === 0 ? (
              <div className="flex items-center justify-center h-48 text-[#9CA3AF] dark:text-[#3D607E] text-sm">
                Aucune donnée salariale disponible
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={Math.min(260, Math.max(140, chartData.length * 30))}>
                <BarChart
                  data={chartData}
                  layout="vertical"
                  margin={{ top: 4, right: 40, left: 8, bottom: 4 }}
                  barCategoryGap="25%"
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#6B7280" strokeOpacity={0.15} horizontal={false} />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 10, fill: "#9CA3AF" }}
                    tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={{ fontSize: 10, fill: "#6B7280" }}
                    width={125}
                    tickLine={false}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: "11px", paddingTop: "4px" }} />
                  <Bar dataKey="Actuel" fill="#94A3B8" radius={[0, 3, 3, 0]} maxBarSize={14} />
                  <Bar dataKey="Nouveau" fill="#1A4D2E" radius={[0, 3, 3, 0]} maxBarSize={14} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Tableau des collaborateurs ── */}
      <Card className="dark:bg-[#0B1628] dark:border-[#1A3050]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 dark:text-[#D6E4F5]">
            <Users className="w-5 h-5 text-[#1A4D2E] dark:text-[#4CAF70]" />
            Collaborateurs — NAO {year}
          </CardTitle>
          <CardDescription>
            Renseignez les nouveaux salaires et primes. L'augmentation en % est calculée automatiquement.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {/* Vue mobile — cartes */}
          <div className="sm:hidden divide-y divide-gray-100 dark:divide-[#1A3050]">
            {collaborateurs.map((c) => {
              const newGross = parseFloat(c.new_gross_salary) || 0;
              const diff = c.current_gross != null ? newGross - c.current_gross : 0;
              const pctNum = parseFloat(c.new_pct) || 0;
              return (
                <div key={c.user_id} className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-3 min-w-0">
                      <Avatar className="w-9 h-9 shrink-0">
                        <AvatarImage src={c.avatar_url} />
                        <AvatarFallback className="text-xs bg-[#1A4D2E]/10 text-[#1A4D2E] dark:bg-[#1A4D2E]/30 dark:text-[#4CAF70] font-semibold">
                          {getInitials(c.full_name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <p className="font-semibold text-sm dark:text-[#D6E4F5] truncate"><ClickableUser userId={c.user_id}>{c.full_name}</ClickableUser></p>
                        <p className="text-xs text-[#9CA3AF] dark:text-[#3D607E] mt-0.5 truncate">
                          {c.position || c.department || "—"}
                        </p>
                      </div>
                    </div>
                    {c.current_gross != null && diff !== 0 && (
                      <span
                        className={`text-xs font-semibold tabular-nums shrink-0 ${
                          diff > 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-500"
                        }`}
                      >
                        {diff > 0 ? "+" : ""}{fmtEuro(diff)}
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-[#9CA3AF] dark:text-[#3D607E] mb-1">Brut actuel</p>
                      <p className="text-sm font-medium dark:text-[#D6E4F5] tabular-nums">
                        {c.current_gross ? fmtEuro(c.current_gross) : "—"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-[#9CA3AF] dark:text-[#3D607E] mb-1">Augmentation %</p>
                      <div className="relative">
                        <Input
                          type="number"
                          value={c.new_pct}
                          onChange={(e) => updatePct(c.user_id, e.target.value)}
                          className={`h-8 text-sm pr-6 dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5] ${
                            pctNum > 0 ? "border-emerald-400/50" : pctNum < 0 ? "border-red-400/50" : ""
                          }`}
                          step="0.1"
                          placeholder="0"
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[#9CA3AF] text-xs">%</span>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-[#9CA3AF] dark:text-[#3D607E] mb-1">Nouveau brut</p>
                      <Input
                        type="number"
                        value={c.new_gross_salary}
                        onChange={(e) => updateGross(c.user_id, e.target.value)}
                        className="h-8 text-sm dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]"
                      />
                    </div>
                    <div>
                      <p className="text-xs text-[#9CA3AF] dark:text-[#3D607E] mb-1">Prime (€)</p>
                      <Input
                        type="number"
                        value={c.new_bonus}
                        onChange={(e) => update(c.user_id, "new_bonus", e.target.value)}
                        placeholder="0"
                        className="h-8 text-sm dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]"
                      />
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-[#9CA3AF] dark:text-[#3D607E] mb-1">Note</p>
                    <Input
                      value={c.new_note}
                      onChange={(e) => update(c.user_id, "new_note", e.target.value)}
                      placeholder="Note..."
                      className="h-8 text-sm dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]"
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Vue desktop — tableau */}
          <div className="hidden sm:block overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="dark:border-[#1A3050]">
                  <TableHead className="dark:text-[#7EA8CE]">Collaborateur</TableHead>
                  <TableHead className="dark:text-[#7EA8CE]">Département</TableHead>
                  <TableHead className="dark:text-[#7EA8CE]">Poste</TableHead>
                  <TableHead className="dark:text-[#7EA8CE]">Brut actuel</TableHead>
                  <TableHead className="dark:text-[#7EA8CE]">Augm. %</TableHead>
                  <TableHead className="dark:text-[#7EA8CE]">Nouveau brut</TableHead>
                  <TableHead className="dark:text-[#7EA8CE]">Prime (€)</TableHead>
                  <TableHead className="dark:text-[#7EA8CE]">Note</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {collaborateurs.map((c) => {
                  const newGross = parseFloat(c.new_gross_salary) || 0;
                  const diff = c.current_gross != null ? newGross - c.current_gross : null;
                  const pctNum = parseFloat(c.new_pct) || 0;

                  return (
                    <TableRow
                      key={c.user_id}
                      className="dark:border-[#1A3050] dark:hover:bg-[#112240]/50"
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Avatar className="w-7 h-7 shrink-0">
                            <AvatarImage src={c.avatar_url} />
                            <AvatarFallback className="text-[10px] bg-[#1A4D2E]/10 text-[#1A4D2E] dark:bg-[#1A4D2E]/30 dark:text-[#4CAF70] font-semibold">
                              {getInitials(c.full_name)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="font-medium dark:text-[#D6E4F5] whitespace-nowrap"><ClickableUser userId={c.user_id}>{c.full_name}</ClickableUser></span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-[#4B5563] dark:text-[#7EA8CE]">
                        {c.department || "—"}
                      </TableCell>
                      <TableCell className="text-sm text-[#4B5563] dark:text-[#7EA8CE]">
                        {c.position || "—"}
                      </TableCell>
                      <TableCell className="dark:text-[#D6E4F5] tabular-nums">
                        {c.current_gross ? fmtEuro(c.current_gross) : "—"}
                      </TableCell>
                      {/* Colonne Augmentation % — éditable, recalcule le brut */}
                      <TableCell>
                        {c.current_gross != null ? (
                          <div className="space-y-1">
                            <div className="relative w-24">
                              <Input
                                type="number"
                                value={c.new_pct}
                                onChange={(e) => updatePct(c.user_id, e.target.value)}
                                step="0.1"
                                placeholder="0"
                                className={`pr-7 dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5] ${
                                  pctNum > 0
                                    ? "border-emerald-400/60 focus-visible:ring-emerald-400/40"
                                    : pctNum < 0
                                    ? "border-red-400/60 focus-visible:ring-red-400/40"
                                    : ""
                                }`}
                              />
                              <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF] text-xs font-medium pointer-events-none">
                                %
                              </span>
                            </div>
                            {diff !== null && diff !== 0 && (
                              <p
                                className={`text-xs tabular-nums font-medium ${
                                  diff > 0
                                    ? "text-emerald-600 dark:text-emerald-400"
                                    : "text-red-500 dark:text-red-400"
                                }`}
                              >
                                {diff > 0 ? "+" : ""}{fmtEuro(diff)}
                              </p>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-[#9CA3AF]">—</span>
                        )}
                      </TableCell>
                      {/* Colonne Nouveau brut — éditable, recalcule le % */}
                      <TableCell>
                        <Input
                          type="number"
                          value={c.new_gross_salary}
                          onChange={(e) => updateGross(c.user_id, e.target.value)}
                          className="w-28 dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          value={c.new_bonus}
                          onChange={(e) => update(c.user_id, "new_bonus", e.target.value)}
                          className="w-24 dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]"
                          placeholder="0"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          value={c.new_note}
                          onChange={(e) => update(c.user_id, "new_note", e.target.value)}
                          className="w-36 dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5]"
                          placeholder="Note..."
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>

              {/* ── Ligne de totaux live ── */}
              <TableFooter>
                <TableRow className="dark:border-[#1A3050] bg-gray-50/80 dark:bg-[#112240]/80">
                  <TableCell colSpan={3} className="py-3 font-semibold text-sm dark:text-[#D6E4F5]">
                    Total
                  </TableCell>
                  <TableCell className="py-3 font-semibold tabular-nums dark:text-[#D6E4F5]">
                    {fmtEuro(totalCurrentMass)}
                  </TableCell>
                  {/* Colonne Augm. % — affiche l'augmentation globale live */}
                  <TableCell className="py-3">
                    <div className="space-y-0.5">
                      <p className={`text-xs font-bold tabular-nums ${
                        globalIncreasePct > 0
                          ? "text-emerald-600 dark:text-emerald-400"
                          : globalIncreasePct < 0
                          ? "text-red-500 dark:text-red-400"
                          : "text-[#9CA3AF]"
                      }`}>
                        {globalIncreasePct > 0 ? "+" : ""}{globalIncreasePct.toFixed(2)} %
                      </p>
                      {masseDiff !== 0 && (
                        <p className={`text-xs tabular-nums ${
                          masseDiff > 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-500"
                        }`}>
                          {masseDiff > 0 ? "+" : ""}{fmtEuro(masseDiff)}
                        </p>
                      )}
                    </div>
                  </TableCell>
                  {/* Colonne Nouveau brut total */}
                  <TableCell className="py-3 font-semibold tabular-nums dark:text-[#D6E4F5]">
                    {fmtEuro(totalNewMass)}
                  </TableCell>
                  {/* Colonne Prime totale */}
                  <TableCell className="py-3 font-semibold tabular-nums dark:text-[#D6E4F5]">
                    {totalBonus > 0 ? fmtEuro(totalBonus) : "—"}
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableFooter>
            </Table>
          </div>

          {/* ── Bandeau de synthèse mobile ── */}
          <div className="sm:hidden border-t-2 border-[#1A4D2E]/20 dark:border-[#1A4D2E]/40 bg-gray-50 dark:bg-[#112240]/60 px-4 py-3 flex items-center justify-between gap-4">
            <div className="text-xs text-[#9CA3AF] dark:text-[#3D607E]">
              <p>Masse actuelle</p>
              <p className="font-semibold text-sm dark:text-[#D6E4F5] tabular-nums mt-0.5">{fmtEuro(totalCurrentMass)}</p>
            </div>
            <div className="text-center">
              <p className={`text-lg font-bold tabular-nums ${
                globalIncreasePct > 0
                  ? "text-emerald-600 dark:text-emerald-400"
                  : globalIncreasePct < 0
                  ? "text-red-500"
                  : "text-[#9CA3AF]"
              }`}>
                {globalIncreasePct > 0 ? "+" : ""}{globalIncreasePct.toFixed(2)}%
              </p>
              {masseDiff !== 0 && (
                <p className={`text-xs tabular-nums ${masseDiff > 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-500"}`}>
                  {masseDiff > 0 ? "+" : ""}{fmtEuro(masseDiff)}
                </p>
              )}
            </div>
            <div className="text-xs text-right text-[#9CA3AF] dark:text-[#3D607E]">
              <p>Après NAO</p>
              <p className="font-semibold text-sm dark:text-[#D6E4F5] tabular-nums mt-0.5">{fmtEuro(totalNewMass)}</p>
            </div>
          </div>
        </CardContent>
      </Card>
      {/* ── Historique des NAO ── */}
      {!histLoading && (history.by_year.length > 0 || history.by_employee.length > 0) && (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold dark:text-[#D6E4F5] flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-[#1A4D2E] dark:text-[#4CAF70]" />
            Historique des NAO
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Masse salariale par année */}
            <Card className="dark:bg-[#0B1628] dark:border-[#1A3050]">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center gap-2 dark:text-[#D6E4F5]">
                  <BarChart3 className="w-4 h-4 text-[#1A4D2E] dark:text-[#4CAF70]" />
                  Masse salariale par année
                </CardTitle>
                <CardDescription className="text-xs">
                  Masse brute totale (€) et taux d'augmentation (%) par NAO
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                {history.by_year.length === 0 ? (
                  <div className="flex items-center justify-center h-40 text-sm text-[#9CA3AF] dark:text-[#3D607E]">
                    Aucune donnée historique disponible
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <ComposedChart
                      data={history.by_year}
                      margin={{ top: 8, right: 44, left: 4, bottom: 4 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#6B7280" strokeOpacity={0.15} />
                      <XAxis dataKey="year" tick={{ fontSize: 11, fill: "#9CA3AF" }} />
                      <YAxis
                        yAxisId="left"
                        tick={{ fontSize: 10, fill: "#9CA3AF" }}
                        tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                        width={46}
                      />
                      <YAxis
                        yAxisId="right"
                        orientation="right"
                        tick={{ fontSize: 10, fill: "#E07A5F" }}
                        tickFormatter={(v) => `${v > 0 ? "+" : ""}${v.toFixed(1)}%`}
                        width={44}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "#fff",
                          border: "1px solid #E5E7EB",
                          borderRadius: "8px",
                          fontSize: "12px",
                        }}
                        formatter={(value, name) => {
                          if (name === "Augm. %")
                            return [`${value > 0 ? "+" : ""}${Number(value).toFixed(2)}%`, name];
                          return [fmtEuro(value), name];
                        }}
                      />
                      <Legend wrapperStyle={{ fontSize: "11px" }} />
                      <Bar
                        yAxisId="left"
                        dataKey="total_mass"
                        name="Masse brute"
                        fill="#1A4D2E"
                        radius={[3, 3, 0, 0]}
                        maxBarSize={44}
                      />
                      {history.by_year.some((y) => y.increase_pct != null) && (
                        <Line
                          yAxisId="right"
                          dataKey="increase_pct"
                          name="Augm. %"
                          type="monotone"
                          stroke="#E07A5F"
                          strokeWidth={2}
                          dot={{ r: 4, fill: "#E07A5F", strokeWidth: 0 }}
                          activeDot={{ r: 6 }}
                        />
                      )}
                    </ComposedChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* Évolution individuelle */}
            <Card className="dark:bg-[#0B1628] dark:border-[#1A3050]">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <CardTitle className="text-sm flex items-center gap-2 dark:text-[#D6E4F5]">
                      <Users className="w-4 h-4 text-[#3B82F6]" />
                      Évolution individuelle
                    </CardTitle>
                    <CardDescription className="text-xs mt-1">
                      Salaire brut par collaborateur au fil des années
                    </CardDescription>
                  </div>
                  {departments.length > 1 && (
                    <Select value={deptFilter} onValueChange={setDeptFilter}>
                      <SelectTrigger className="w-36 h-8 text-xs dark:bg-[#112240] dark:border-[#1A3050] dark:text-[#D6E4F5] shrink-0">
                        <SelectValue placeholder="Département" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Tous</SelectItem>
                        {departments.map((d) => (
                          <SelectItem key={d} value={d}>{d}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                {pivotData.length === 0 || filteredEmployees.length === 0 ? (
                  <div className="flex items-center justify-center h-40 text-sm text-[#9CA3AF] dark:text-[#3D607E]">
                    Aucune donnée historique disponible
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart
                      data={pivotData}
                      margin={{ top: 8, right: 16, left: 4, bottom: 4 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#6B7280" strokeOpacity={0.15} />
                      <XAxis dataKey="year" tick={{ fontSize: 11, fill: "#9CA3AF" }} />
                      <YAxis
                        tick={{ fontSize: 10, fill: "#9CA3AF" }}
                        tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                        width={40}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "#fff",
                          border: "1px solid #E5E7EB",
                          borderRadius: "8px",
                          fontSize: "11px",
                        }}
                        formatter={(value, name) => [fmtEuro(value), name]}
                      />
                      <Legend
                        wrapperStyle={{ fontSize: "10px" }}
                        iconType="circle"
                        iconSize={8}
                      />
                      {filteredEmployees.map((emp, i) => (
                        <Line
                          key={emp.user_id}
                          type="monotone"
                          dataKey={`u_${emp.user_id}`}
                          name={
                            emp.full_name.length > 18
                              ? emp.full_name.slice(0, 16) + "…"
                              : emp.full_name
                          }
                          stroke={LINE_COLORS[i % LINE_COLORS.length]}
                          strokeWidth={1.5}
                          dot={{ r: 3 }}
                          activeDot={{ r: 5 }}
                          connectNulls
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default NAOPage;
